import React from 'react';
import { ConversationChannelIcon } from '../conversationChannelIcons';
import styles from './ConversationChannelBadge.module.css';

interface ConversationChannelBadgeProps {
  icon: ConversationChannelIcon;
  title?: string;
  compact?: boolean;
  size?: 'default' | 'compact' | 'overlay' | 'avatar' | 'headerAvatar' | 'messageAvatar';
  disableTooltip?: boolean;
  inlineLabel?: string;
}

const getIconSvg = (kind: ConversationChannelIcon['kind']) => {
  switch (kind) {
    case 'internal':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <circle cx="3.5" cy="8" r="1.5" />
          <circle cx="12.5" cy="4" r="1.5" />
          <circle cx="12.5" cy="12" r="1.5" />
          <path d="M5 8h4.5M10.8 5.2L5 7.3M10.8 10.8L5 8.7" />
        </svg>
      );
    case 'employee':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <circle cx="5.25" cy="5.5" r="2.25" />
          <circle cx="11.25" cy="6.5" r="1.75" />
          <path d="M2.5 12c.7-1.7 2-2.6 3.9-2.6 1.8 0 3.1.9 3.8 2.6" />
          <path d="M9.2 12c.45-1.15 1.35-1.8 2.7-1.8.76 0 1.45.2 2.1.68" />
        </svg>
      );
    case 'ai':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <path d="M8 1.8l1.15 2.9 2.85 1.15-2.85 1.15L8 9.9 6.85 7 4 5.85 6.85 4.7 8 1.8Z" />
          <path d="M12.1 9.8l.62 1.58 1.58.62-1.58.63-.62 1.57-.62-1.57-1.58-.63 1.58-.62.62-1.58Z" />
          <path d="M3.2 9.9l.46 1.12 1.12.46-1.12.47-.46 1.11-.47-1.11-1.11-.47 1.11-.46.47-1.12Z" />
        </svg>
      );
    case 'lead':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <circle cx="6" cy="6" r="2.5" />
          <path d="M2.8 12.2c.8-1.9 2.2-2.9 4.2-2.9 1.2 0 2.24.35 3.08 1.03" />
          <path d="M11.5 2.2l.54 1.1 1.21.18-.88.86.21 1.2-1.08-.57-1.08.57.2-1.2-.88-.86 1.22-.18.54-1.1Z" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'customer':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <circle cx="8" cy="5.25" r="2.5" />
          <path d="M3.2 12.4c1.05-2.1 2.65-3.15 4.8-3.15 2.14 0 3.74 1.05 4.8 3.15" />
        </svg>
      );
    case 'phone':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <path d="M5 2.9c.24-.26.58-.4.93-.37l1.75.13c.42.03.77.32.89.73l.46 1.58c.1.35.03.73-.2 1.02L7.9 7.16a10.28 10.28 0 0 0 .95 1.2c.38.39.79.73 1.22 1.02l1.18-.94c.29-.23.67-.3 1.02-.2l1.58.46c.41.12.7.47.73.89l.13 1.75c.03.35-.11.69-.37.93l-1.18 1.08c-.41.37-1 .52-1.54.35-1.69-.53-3.28-1.61-4.76-3.09S4.3 6.56 3.76 4.87c-.17-.54-.02-1.13.35-1.54L5 2.9Z" />
        </svg>
      );
    case 'whatsApp':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <path d="M8 2.2a5.5 5.5 0 0 1 4.92 7.95L13.6 13l-2.93-.64A5.5 5.5 0 1 1 8 2.2Z" />
          <path d="M6.3 5.45c.16-.18.4-.27.64-.25l.73.06c.28.02.51.2.59.47l.24.82c.07.24.02.51-.14.7l-.43.55c.2.35.44.67.72.95.27.27.58.5.93.7l.57-.45c.2-.16.46-.21.71-.14l.81.24c.27.08.46.31.48.59l.06.72c.02.25-.07.49-.25.66l-.54.49c-.29.27-.71.36-1.09.24A5.68 5.68 0 0 1 7.2 9.7a5.68 5.68 0 0 1-1.69-2.6c-.12-.38-.03-.8.24-1.09l.55-.56Z" />
        </svg>
      );
    case 'instagram':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="3.2" />
          <circle cx="8" cy="8" r="2.6" />
          <circle cx="11.3" cy="4.8" r="0.8" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'messenger':
      return (
        <svg viewBox="0 0 16 16" fill="none" className={styles.svg}>
          <path
            d="M8 1.25a6.75 6.75 0 0 0-5.89 10.15L1.75 14.25l3.2-.86A6.75 6.75 0 1 0 8 1.25Z"
            fill="currentColor"
            fillOpacity="0.16"
            stroke="currentColor"
            strokeWidth="1.15"
            strokeLinejoin="round"
          />
          <path
            d="M9.35 5.1 4.9 8.15c-.2.14-.06.46.2.42l2.2-.4-.88 2.45c-.08.22.2.35.36.2l3.85-3.2c.16-.14.09-.42-.12-.42H7.45l1.25-1.9c.1-.16-.05-.35-.22-.3Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'telegram':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <path d="M13.7 3.2 2.9 7.4c-.49.19-.47.9.04 1.06l2.56.8.97 2.94c.16.49.81.56 1.07.12l1.44-2.4 2.62 1.95c.37.28.9.06.97-.39l1.54-7.37c.1-.51-.38-.92-.86-.73Z" />
          <path d="m6.46 9.3 6.86-5.53" />
        </svg>
      );
    case 'webchat':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <path d="M4 3.8h8a2.2 2.2 0 0 1 2.2 2.2v4.2A2.2 2.2 0 0 1 12 12.2H8.6L5.8 14.5V12.2H4A2.2 2.2 0 0 1 1.8 10V6A2.2 2.2 0 0 1 4 3.8Z" />
          <circle cx="6" cy="7.8" r="0.75" fill="currentColor" stroke="none" />
          <circle cx="8" cy="7.8" r="0.75" fill="currentColor" stroke="none" />
          <circle cx="10" cy="7.8" r="0.75" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg viewBox="0 0 16 16" fill="currentColor" className={styles.svg}>
          <path d="M10.2 2.2h1.4v2.4c.9-.1 1.7.1 2.4.6V5.4c-.7-.4-1.6-.6-2.4-.5V8.8c0 1.8-1.2 3-2.9 3-1.7 0-2.9-1.2-2.9-2.9s1.2-2.9 2.9-2.9c.2 0 .5 0 .7.1V7c-.2-.1-.4-.1-.7-.1-.9 0-1.5.6-1.5 1.5s.6 1.5 1.5 1.5 1.5-.6 1.5-1.5V2.2Z" />
        </svg>
      );
    case 'signal':
      return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" className={styles.svg}>
          <path d="M5 12.6 2.8 13l.44-2.05A4.95 4.95 0 0 1 3 9.5C3 6.74 5.24 4.5 8 4.5s5 2.24 5 5-2.24 5-5 5c-1.12 0-2.16-.37-3-.99Z" />
          <path d="M5.25 3.95 4.7 3.4M7.3 2.92l-.08-.78M9.52 2.98l.18-.76M11.45 4l.56-.53M12.85 5.8l.74-.14M12.88 8.13l.75.1M12 10.3l.63.45M10.5 11.88l.29.7M8.2 12.44l-.02.76M5.95 11.96l-.37.66M4.2 10.65l-.62.45" />
        </svg>
      );
    case 'other':
      return (
        <svg viewBox="0 0 16 16" fill="currentColor" className={styles.svg}>
          <circle cx="3.25" cy="8" r="1.35" />
          <circle cx="8" cy="8" r="1.35" />
          <circle cx="12.75" cy="8" r="1.35" />
        </svg>
      );
    default:
      return null;
  }
};

const ConversationChannelBadge: React.FC<ConversationChannelBadgeProps> = ({
  icon,
  title,
  compact = false,
  size,
  disableTooltip = false,
  inlineLabel,
}) => {
  const kindClassName = styles[`badge${icon.kind.charAt(0).toUpperCase()}${icon.kind.slice(1)}`] ?? '';
  const hasInlineLabel = Boolean(inlineLabel?.trim());
  const resolvedSize = size ?? (compact ? 'compact' : 'default');
  const sizeClassName = resolvedSize === 'overlay'
    ? styles.overlay
    : resolvedSize === 'compact'
      ? styles.compact
      : resolvedSize === 'avatar'
        ? styles.avatar
        : resolvedSize === 'headerAvatar'
          ? styles.headerAvatar
          : resolvedSize === 'messageAvatar'
            ? styles.messageAvatar
            : '';

  return (
    <span className={`${styles.wrapper} ${sizeClassName} ${hasInlineLabel ? styles.withInlineLabel : ''}`}>
      <span
        className={`${styles.badge} ${kindClassName} ${hasInlineLabel ? styles.badgeWithInlineLabel : ''}`}
        role="img"
        tabIndex={0}
        aria-label={title ?? icon.label}
      >
        {getIconSvg(icon.kind)}
        {hasInlineLabel ? <span className={styles.inlineLabel}>{inlineLabel}</span> : null}
      </span>
      {!disableTooltip && title && (
        <span className={styles.tooltip} aria-hidden="true">
          {title}
        </span>
      )}
    </span>
  );
};

export default ConversationChannelBadge;